import { attr, belongsTo } from "@ember-data/model";
import { service } from "@ember/service";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type Vaccine from "parog-web/models/vaccine";
import type { SemeiaMedicSession } from "parog-web/services/session";
import type {
  DialysisCareLocationStringEnum,
  DialysisTypeStringEnum
} from "parog-web/utils/imports/correspondance-tables";

type CytomegalovirusStatusStringEnum =
  | "d_plus_r_plus"
  | "d_plus_r_minus"
  | "d_minus_r_plus"
  | "d_minus_r_minus";

export const mrcStageValues = [
  "stage-undefined",
  "stage-1",
  "stage-2",
  "stage-3",
  "stage-3-a",
  "stage-3-b",
  "stage-4",
  "stage-5"
] as const;

export type MrcStageStringEnum = (typeof mrcStageValues)[number];

type HemodialysisType = "at-home" | "in-udm" | "in-self-dialysis-unit";

type TransplantedOrganStringEnum =
  | "bipulmonary"
  | "kidney"
  | "kidney-pancreas"
  | "kidney-liver"
  | "kidney-lung"
  | "kidney-lung-heart"
  | "kidney-heart-liver"
  | "liver-lung"
  | "lung-kidney"
  | "lung-heart"
  | "monopulmonary";

type AbmClassificationStringEnum =
  | "total_incapacity"
  | "third_party_need"
  | "autonomous_walk";

type DonorCreatinineStringEnum = "unknown" | "lte_190" | "gt_190";

type HtaStringEnum = "treated" | "yes" | "no" | "unknown";
type comorbiditiesStringEnum = "true" | "false" | "unknown";
type DoSportStringEnum = "true" | "false" | "stoped";
type FamilyIcBackgroundEnum = "true" | "false" | "under_55";

type HormoneReceptor = {
  percentage: number;
  intensity: "low" | "medium" | "high";
};

type TnmClassification = {
  t: string;
  n: string;
  m: string;
};

type StageStringEnum = "1" | "2" | "3" | "4" | "5";

type GleasonScoreStringEnum =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type HistologyStringEnum = "CIC" | "CCI" | "CLI";

type Her2StringEnum = "positive" | "negative";

type OncogeneticConsultationStringEnum = "yes" | "no" | "isExpected";

type PelvicSonogramStringEnum = "normal" | "abnormal" | "not-applicable";

type HematoCancerTypeStringEnum = "leukemia" | "myeloma" | "lymphoma";

type PancreaticCancerTypeStringEnum =
  | "adenocarcinoma"
  | "ampullome"
  | "neuroendocrine-tumour";

type Child = {
  gender: string;
  birthyear: Date;
  city: string;
};

type MainDiagnosisStringEnum =
  | "bipolarDisorderType1"
  | "bipolarDisorderType2"
  | "cyclothimicDisorder"
  | "lightDepression"
  | "mediumDepression"
  | "persistingDepression";

type MaritalStatus = "single" | "couple" | "separated" | "widowed";

type ProfessionalActivity =
  | "unemployed"
  | "activeFullTime"
  | "activePartTime"
  | "retired"
  | "student"
  | "volonteer";

type Housing =
  | "single"
  | "parentsPlace"
  | "ownFamilyHome"
  | "childrenHome"
  | "anotherFamilyMember"
  | "houseShare"
  | "community";

type ProstateCancerTypeEnum = "adenocarcinoma";

type nonSmallCellCancerTypeEnum =
  | "adenocarcinoma"
  | "squamous-cell-carcinoma"
  | "large-cell-carcinoma";

type DialysisMachineTypeStringEnum =
  | "PHYSIDIA-S3"
  | "FRESENIUS-4008S"
  | "FRESENIUS-4008H"
  | "FRESENIUS-5008S"
  | "FRESENIUS-5008-CorDiax"
  | "FRESENIUS-6008"
  | "NIKKIS-DBB-05"
  | "B.-BRAU-Dialog-plus"
  | "NXSTAGE-System-One"
  | "NXSTAGE-Versi-HD"
  | "GAMBRO-PRISMAFLEX"
  | "NEXTKIDNEY-NEOKIDNEY"
  | "FRESENIUS-SLEEPSAFE-HARMONY"
  | "BAXTER-EXTRANEAL"
  | "BAXTER-DIANEAL"
  | "BAXTER-NUTRINEAL"
  | "BAXTER-PHYSIONEAL";

type DialysisCareTypeStringEnum = "DPCA" | "DPA";

export type surgery = {
  year: Date;
  surgeries: string[];
};

export default class MedicalHistoryChangedJournalEvent extends JournalEvent {
  static override type = "medical-history-changed-journal-event";

  // Oncowise specific
  @service declare session: SemeiaMedicSession;

  @attr("string") declare abmClassification: AbmClassificationStringEnum;
  @attr("string") declare activity: string;
  @attr("number") declare albuminuria: number;
  @attr("string") declare alcohol: string;
  @attr("boolean") declare arglp1: BooleanConstructor;
  @attr() declare articular: string[];
  @attr("string") declare assessmentRealisation: string;
  @attr("string") declare assessmentResult: string;
  @attr("string") declare autoImmuneInflammatory: string;
  @attr("boolean", { allowNull: true }) declare brca1: boolean;
  @attr() declare brca1Member: string[];
  @attr("boolean", { allowNull: true }) declare brca2: boolean;
  @attr() declare brca2Member: string[];
  @attr("string") declare cardiovascular: string;
  @attr("string", { allowNull: true }) declare cellCancerType: string;
  @attr("children", {
    defaultValue() {
      return [];
    }
  })
  declare children: Child[];
  @attr("boolean", { allowNull: true })
  declare chronicBronchopneumopathy: boolean;
  @attr("string")
  declare comments: string;
  @attr("boolean", { allowNull: true }) declare contraception: boolean;
  @attr("number") declare creatinineThreeMonthsAfterTransplant: number;
  // LGC specific
  @attr("string") declare cumulativeSpreadDisease: string;
  @attr("string")
  declare cytomegalovirusStatus: CytomegalovirusStatusStringEnum;
  @attr("number") declare dailyCigarettesNumber: number;
  @attr("number") declare dfg: number;
  @attr("date") declare diagnosisDate: Date;
  @attr("string") declare diagnosticAge: string;
  @attr("date") declare diagnosticDate: Date;
  @attr("string") declare diagnosticType: string;
  @attr("string") declare dialysisCareLocation: DialysisCareLocationStringEnum;
  @attr("string") declare dialysisCareType: DialysisCareTypeStringEnum;
  @attr("string") declare dialysisMachineType: DialysisMachineTypeStringEnum;
  @attr("string") declare dialysisStartDate: Date;
  @attr("string") declare dialysisType: DialysisTypeStringEnum;
  @attr("number") declare diastolic: number;
  @attr() declare digestiveSurgery: surgery[];
  @attr("string") declare diseaseActivity: string;
  @attr("string") declare donorCreatinine: DonorCreatinineStringEnum;
  @attr("string") declare donorType: string;
  @attr("string") declare doSport: DoSportStringEnum;
  @attr("boolean") declare dyslipidemieTreated: boolean;
  @attr() declare estrogenReceptor: HormoneReceptor;
  @attr("string") declare evolution: string;
  @attr("boolean") declare exp: boolean;
  @attr("boolean", { allowNull: true })
  declare familialPancreaticCancer: boolean;
  @attr("boolean", { allowNull: true })
  declare familyHistoryBreastCancer: boolean;
  @attr() declare familyHistoryBreastCancerMember: string[];
  @attr("boolean", { allowNull: true })
  declare familyHistoryOvarianCancer: boolean;
  @attr() declare familyHistoryOvarianCancerMember: string[];
  @attr("boolean", { allowNull: true })
  declare familyHistoryProstateCancer: boolean;
  @attr() declare familyHistoryProstateCancerMember: string[];
  @attr("string") declare familyIcBackground: FamilyIcBackgroundEnum;
  @attr("string") declare familyMici: string;
  @attr("string") declare familyOther: string;
  @attr("boolean", { allowNull: true }) declare fertilityPreservation: boolean;
  @attr("number") declare fevg: number;
  @attr("string") declare gleasonScore: GleasonScoreStringEnum;
  @attr("string") declare gynecoObstetric: string;
  @attr("boolean", { allowNull: true }) declare hasChildren: boolean;
  @attr("number") declare hba: number;
  @attr("date") declare hbaDate: Date;
  @attr("string") declare heartFailureType: string;
  @attr("number") declare height: number;
  @attr("string") declare hematoCancerType: HematoCancerTypeStringEnum;
  @attr("string") declare hemodialysisType: HemodialysisType;
  @attr("string") declare her2: Her2StringEnum;
  @attr("boolean", { allowNull: true })
  declare hereditaryChronicPrss1GeneMutation: boolean;
  @attr("string") declare histology: HistologyStringEnum;
  @attr("string") declare hobbies: string;
  @attr("string") declare homeHelp: string;
  @attr("boolean", { allowNull: true }) declare hormoneSensitiveCancer: boolean;
  @attr("string") declare housing: Housing;
  @attr("string") declare hta: HtaStringEnum;
  @attr("string") declare ic: comorbiditiesStringEnum;
  @attr("boolean") declare idpp4: boolean;
  @attr("boolean", { allowNull: true }) declare immunologicalRisk: boolean;
  @attr("boolean", { allowNull: true }) declare incompatibleAbo: boolean;
  @attr("string") declare infectious: string;
  @attr("boolean") declare insulin: boolean;
  @attr("boolean") declare isFollowingDiabetologist: boolean;
  @attr("boolean") declare isglt2: boolean;
  @attr("string") declare job: string;
  @attr("number") declare ki67: number;
  @attr("number") declare lastDyslipidemie: number;
  @attr("string") declare lifestyle: string;
  @attr("string") declare lifestyleComment: string;
  @attr("string") declare localisation: string;
  @attr("boolean", { allowNull: true }) declare lungCancerFamily: boolean;
  @attr("string") declare mainDiagnosis: MainDiagnosisStringEnum;
  @attr("string") declare maritalStatus: MaritalStatus;
  @attr("string") declare mcva: comorbiditiesStringEnum;
  @attr("boolean", { allowNull: true }) declare menopause: boolean;
  @attr("boolean") declare met: boolean;
  @attr("string") declare mrc: comorbiditiesStringEnum;
  @attr("boolean", { allowNull: true })
  declare mrcPreexistingTransplant: boolean;
  @attr("string") declare mrcStage: MrcStageStringEnum;
  @attr() declare mutation: string[];
  @attr("string") declare nephropatie: string;
  @attr("string") declare neur: comorbiditiesStringEnum;
  @attr("string") declare nonMiciTreatmentsComment: string;
  @attr("string", { allowNull: true })
  declare nonSmallCellCancerType: nonSmallCellCancerTypeEnum;
  @attr("number") declare numberOfChildren: number;
  // Mentalwise specific
  @attr("number") declare numberOfTs: number;
  @attr("string") declare nyhaStage: string;
  @attr("string")
  declare oncogeneticConsultation: OncogeneticConsultationStringEnum;
  @attr("string") declare oncological: string;
  @attr("string") declare ophtalmicSymptoms: string;
  @attr("string") declare oralLesions: string;
  @attr("string") declare oth: comorbiditiesStringEnum;
  @attr("boolean", { allowNull: true }) declare otherCancer: boolean;
  @attr() declare otherCancerType: string[];
  @attr("string") declare otherPersonal: string;
  @attr("string") declare otherSymptoms: string;
  @attr("string") declare pancreaticCancerType: PancreaticCancerTypeStringEnum;
  @attr() declare pancreaticMutation: string[];
  @belongsTo("patient", {
    async: false,
    inverse: "medicalHistoryChangedJournalEvent"
  })
  declare patient: Patient;
  @attr("string") declare pelvicSonogram: PelvicSonogramStringEnum;
  @attr("string") declare predominantEatingHabit: string;
  @attr() declare proctologicalSurgery: surgery[];
  @attr("string") declare professionalActivity: ProfessionalActivity;
  @attr() declare progesteroneReceptor: HormoneReceptor;
  @attr("string") declare prostateCancerType: ProstateCancerTypeEnum;
  @attr() declare prostateMutations: string[];
  @attr("number") declare rac: number;
  @attr("string") declare registrationTransplantWaitingList: string;
  @attr("number") declare rejectsFirstYear: number;
  @attr("boolean", { allowNull: true }) declare relapse: boolean;
  @attr("string") declare reti: comorbiditiesStringEnum;
  @attr("boolean", {
    allowNull: true
  })
  declare sclerosingCholangitis: boolean;
  @attr("string") declare side: "left" | "right";
  @attr("string") declare skinLesions: string;
  @attr("boolean") declare smoker: number;
  @attr("string") declare smokerDetails: string;
  @attr("date") declare smokingEndDate: Date;
  @attr("boolean", { allowNull: true }) declare smokingPatients: boolean;
  @attr("date") declare smokingStartDate: Date;
  @attr("string") declare stage: StageStringEnum;
  @attr("number") declare state: number;
  @attr("boolean", { allowNull: true }) declare statutPDL1: boolean;
  @attr("date") declare suicidalThoughtsStartDate: Date;
  @attr("boolean", {
    allowNull: true
  })
  declare suicideRisk: boolean;
  @attr("number") declare systolic: number;
  @attr() declare tnmClassification: TnmClassification;
  @attr("string") declare tnmComment: string;
  // Nephrowise specific
  @attr("date") declare transplantDate: Date;
  @attr("string") declare transplantedOrgan: TransplantedOrganStringEnum;
  @attr("string") declare transplantRank: string;
  @attr("boolean", { allowNull: true }) declare tripleNegative: boolean;
  @attr("string") declare underlyingDisease: string;
  @attr("string") declare usualEatingHabit: string;
  @attr("vaccines")
  declare vaccines: Vaccine[];
  @attr("number") declare weight: number;
  @attr("number") declare year: number;

  get hormoneReceptors(): "positive" | "negative" | undefined {
    if (!this.estrogenReceptor || !this.progesteroneReceptor) {
      return undefined;
    }

    const estrogenReceptor = this.estrogenReceptor;
    const progesteroneReceptor = this.progesteroneReceptor;

    function isEstrogenPositive() {
      return estrogenReceptor && estrogenReceptor.percentage > 0;
    }
    function isProgesteronePositive() {
      return progesteroneReceptor && progesteroneReceptor.percentage > 0;
    }
    function isEstrogenNegative() {
      return estrogenReceptor && estrogenReceptor.percentage == 0;
    }
    function isProgesteroneNegative() {
      return progesteroneReceptor && progesteroneReceptor.percentage == 0;
    }

    // If at least on receptor > 0 then positive
    if (isEstrogenPositive() || isProgesteronePositive()) {
      return "positive";
    }
    // If the two receptors === 0 then negative
    else if (isEstrogenNegative() && isProgesteroneNegative()) {
      return "negative";
    }
    return undefined;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "medical-history-changed-journal-event": MedicalHistoryChangedJournalEvent;
  }
}
